/**
 * In-memory implementation of the full {@link Coordinator} API. It is the reference behaviour:
 * the real coordinator (WP-02) must pass the same contract tests (`@botallion/contracts/testing`).
 *
 * Use it to build and test against before the real coordinator exists:
 *
 * ```ts
 * import { createMockCoordinator, createManualClock } from "@botallion/contracts/mock";
 * const clock = createManualClock("2026-10-02T09:00:00.000Z");
 * const coordinator = createMockCoordinator({ now: clock.now });
 * ```
 *
 * Time only moves when `now()` says so, and timeouts (session idle/expiry, claim leases) are applied
 * lazily at the start of every call, so tests are deterministic. `readMessages` with `waitMs` is the
 * one place real timers are used.
 */
import type { z } from "zod";
import { LIVE_CLAIM_STATUSES, type IntentClaim } from "./claim.ts";
import {
  COORDINATOR_DEFAULTS,
  CoordinatorError,
  AmendPlanInputSchema,
  ApprovePlanInputSchema,
  ApprovePromotionInputSchema,
  CheckOverlapInputSchema,
  DeclareClaimInputSchema,
  DispatchPackageInputSchema,
  GetPlanInputSchema,
  GetWorkPackageInputSchema,
  HeartbeatInputSchema,
  ListClaimsInputSchema,
  ListOverlapsInputSchema,
  ListResolutionsInputSchema,
  ListSessionsInputSchema,
  PostMessageInputSchema,
  ReadMessagesInputSchema,
  RecordResolutionInputSchema,
  RegisterSessionInputSchema,
  ReleaseClaimInputSchema,
  ReportLandResultInputSchema,
  RequestLandInputSchema,
  SubmitPlanInputSchema,
  SubscribeOptionsSchema,
  type AmendPlanInput,
  type ApprovePlanInput,
  type ApprovePromotionInput,
  type CheckOverlapInput,
  type CheckOverlapResult,
  type Coordinator,
  type CoordinatorSnapshot,
  type DeclareClaimInput,
  type DeclareClaimResult,
  type DispatchPackageInput,
  type GetPlanInput,
  type GetWorkPackageInput,
  type HeartbeatInput,
  type IngestDiffResult,
  type IngestVerdictResult,
  type ListClaimsInput,
  type ListOverlapsInput,
  type ListResolutionsInput,
  type ListSessionsInput,
  type PostMessageInput,
  type ReadMessagesInput,
  type RecordResolutionInput,
  type RegisterSessionInput,
  type ReleaseClaimInput,
  type ReportLandResultInput,
  type RequestLandInput,
  type SubmitPlanInput,
  type SubscribeOptions,
  type Subscription,
} from "./coordinator.ts";
import { amendPlanRecord } from "./amend.ts";
import type { PlatformEvent, PlatformEventOf, PlatformEventType } from "./events.ts";
import { DiffSummarySchema, type DiffFile, type DiffSummary, type Hunk } from "./git.ts";
import type { LandRequest, MigrationLane } from "./land.ts";
import { detectScopeOverlaps, scopeCoversPath } from "./matching.ts";
import { BROADCAST, type Message } from "./message.ts";
import { UNRESOLVED_OVERLAP_STATUSES, type Overlap, type OverlapSeverity } from "./overlap.ts";
import type { WorkPackage, WorkPackageStatus, WorkPlan } from "./plan.ts";
import { COORDINATOR_PRINCIPAL, isHumanPrincipal, isSystemPrincipal, type Scope } from "./primitives.ts";
import type { Resolution } from "./resolution.ts";
import { AgentRunSchema, type AgentRun } from "./run.ts";
import type { Session } from "./session.ts";
import { DeployRecordSchema, type DeployRecord } from "./stack.ts";
import { VerdictSchema, type Verdict } from "./verdict.ts";

/** Options for {@link createMockCoordinator}. All durations in milliseconds. */
export interface MockCoordinatorOptions {
  /** Clock, as epoch milliseconds. Defaults to `Date.now`. Use {@link createManualClock} in tests. */
  now?: () => number;
  /** Defaults to `COORDINATOR_DEFAULTS.idleAfterMs`. */
  idleAfterMs?: number;
  /** Defaults to `COORDINATOR_DEFAULTS.sessionTtlMs`. */
  sessionTtlMs?: number;
  /** Defaults to `COORDINATOR_DEFAULTS.claimTtlMs`. */
  claimTtlMs?: number;
  /** Defaults to `COORDINATOR_DEFAULTS.maxClaimTtlMs`. */
  maxClaimTtlMs?: number;
}

/** A clock you move by hand. */
export interface ManualClock {
  /** Current time, epoch milliseconds. Pass this as `now`. */
  now: () => number;
  /** Move forward. */
  advance(ms: number): void;
  /** Jump to a time (ISO string or epoch ms). */
  set(time: string | number): void;
  /** Current time as an ISO string. */
  iso(): string;
}

/** Create a {@link ManualClock}. Defaults to the sprint start, 2026-10-02T00:00:00.000Z. */
export function createManualClock(start: string | number = "2026-10-02T00:00:00.000Z"): ManualClock {
  let t = typeof start === "number" ? start : Date.parse(start);
  return {
    now: () => t,
    advance: (ms) => {
      t += ms;
    },
    set: (time) => {
      t = typeof time === "number" ? time : Date.parse(time);
    },
    iso: () => new Date(t).toISOString(),
  };
}

/** Create an in-memory coordinator. */
export function createMockCoordinator(options: MockCoordinatorOptions = {}): MockCoordinator {
  return new MockCoordinator(options);
}

type Listener = { types: Set<PlatformEventType> | null; fn: (event: PlatformEvent) => void };
type Party = "A" | "B";

const clone = <T>(v: T): T => structuredClone(v);
const short = (sha: string) => sha.slice(0, 7);

function parse<S extends z.ZodType>(schema: S, input: unknown): z.infer<S> {
  const result = schema.safeParse(input);
  if (!result.success) {
    const summary = result.error.issues.map((i) => `${i.path.join(".") || "(input)"}: ${i.message}`).join("; ");
    throw new CoordinatorError("invalid_input", summary, result.error.issues);
  }
  return result.data;
}

function hunksIntersect(a: Hunk[], b: Hunk[]): boolean {
  for (const x of a) {
    for (const y of b) {
      const x1 = x.oldStart + Math.max(x.oldLines, 1);
      const y1 = y.oldStart + Math.max(y.oldLines, 1);
      if (x.oldStart < y1 && y.oldStart < x1) return true;
    }
  }
  return false;
}

/** The in-memory coordinator. Construct with {@link createMockCoordinator}. */
export class MockCoordinator implements Coordinator {
  private readonly clock: () => number;
  private readonly idleAfterMs: number;
  private readonly sessionTtlMs: number;
  private readonly claimTtlMs: number;
  private readonly maxClaimTtlMs: number;

  private readonly counters = new Map<string, number>();
  private readonly sessions = new Map<string, Session>();
  private readonly claims = new Map<string, IntentClaim>();
  private readonly claimTtl = new Map<string, number>();
  private readonly overlaps = new Map<string, Overlap>();
  private readonly messages: Message[] = [];
  private readonly resolutions: Resolution[] = [];
  private readonly plans = new Map<string, WorkPlan>();
  private readonly packageSpec = new Map<string, string>();
  private readonly latestDiff = new Map<string, DiffSummary>();
  private readonly verdicts: Verdict[] = [];
  private readonly lands = new Map<string, LandRequest>();
  private readonly deploys = new Map<string, DeployRecord>();
  private readonly runs = new Map<string, AgentRun>();
  private lane: MigrationLane = { holder: null, queue: [] };
  private readonly log: PlatformEvent[] = [];
  private readonly listeners = new Set<Listener>();
  private readonly messageWaiters = new Set<() => void>();

  constructor(options: MockCoordinatorOptions = {}) {
    this.clock = options.now ?? Date.now;
    this.idleAfterMs = options.idleAfterMs ?? COORDINATOR_DEFAULTS.idleAfterMs;
    this.sessionTtlMs = options.sessionTtlMs ?? COORDINATOR_DEFAULTS.sessionTtlMs;
    this.claimTtlMs = options.claimTtlMs ?? COORDINATOR_DEFAULTS.claimTtlMs;
    this.maxClaimTtlMs = options.maxClaimTtlMs ?? COORDINATOR_DEFAULTS.maxClaimTtlMs;
  }

  // ---------------------------------------------------------------------------
  // Mock-only helpers
  // ---------------------------------------------------------------------------

  /** Every event emitted so far, oldest first. Handy for recording a run for dashboard replay. */
  events(): PlatformEvent[] {
    return clone(this.log);
  }

  /** Apply time-based transitions now (idle, expiry, lease lapses). Every API call does this first. */
  sweep(): void {
    const now = this.clock();
    for (const s of this.sessions.values()) {
      if (s.status !== "active" && s.status !== "idle") continue;
      const age = now - Date.parse(s.lastHeartbeat);
      if (age > this.sessionTtlMs) this.expireSession(s);
      else if (age > this.idleAfterMs && s.status === "active") {
        s.status = "idle";
        this.emit("session.updated", { session: s });
      }
    }
    for (const c of this.claims.values()) {
      if (LIVE_CLAIM_STATUSES.includes(c.status) && Date.parse(c.expiresAt) <= now) this.endClaim(c, "expired");
    }
    this.settle();
  }

  // ---------------------------------------------------------------------------
  // Sessions
  // ---------------------------------------------------------------------------

  async registerSession(input: RegisterSessionInput): Promise<Session> {
    const data = parse(RegisterSessionInputSchema, input);
    this.sweep();
    const pkg = this.findPackage(data.wpId);
    if (pkg && (pkg.status === "draft" || pkg.status === "approved")) {
      throw new CoordinatorError("precondition_failed", `${data.wpId} has not been dispatched yet`);
    }
    if (pkg && pkg.status === "landed") throw new CoordinatorError("precondition_failed", `${data.wpId} has already landed`);
    if (pkg && pkg.status === "superseded") throw new CoordinatorError("precondition_failed", `${data.wpId} was superseded by a plan amendment`);
    const ts = this.iso();
    const session: Session = { id: this.nextId("ses"), ...data, status: "active", startedAt: ts, lastHeartbeat: ts, note: null };
    this.sessions.set(session.id, session);
    this.emit("session.registered", { session });
    if (pkg) {
      this.updatePackage(pkg, { sessionId: session.id, ...(pkg.status === "dispatched" ? { status: "in_progress" } : {}) });
    }
    return clone(session);
  }

  async heartbeat(input: HeartbeatInput): Promise<Session> {
    const data = parse(HeartbeatInputSchema, input);
    this.sweep();
    const s = this.requireLiveSession(data.sessionId);
    const now = this.clock();
    const wasActive = s.status === "active";
    s.lastHeartbeat = this.iso();
    s.status = "active";
    if (data.note !== undefined) s.note = data.note;
    for (const c of this.liveClaimsOf(s.id)) {
      const renewed = now + (this.claimTtl.get(c.id) ?? this.claimTtlMs);
      if (renewed > Date.parse(c.expiresAt)) c.expiresAt = new Date(renewed).toISOString();
    }
    this.emit("session.heartbeat", { sessionId: s.id, lastHeartbeat: s.lastHeartbeat, note: s.note });
    if (!wasActive) this.emit("session.updated", { session: s });
    return clone(s);
  }

  async listSessions(input: ListSessionsInput = {}): Promise<Session[]> {
    const data = parse(ListSessionsInputSchema, input);
    this.sweep();
    const statuses = data.statuses ?? ["active", "idle"];
    return clone([...this.sessions.values()].filter((s) => statuses.includes(s.status) && (!data.wpId || s.wpId === data.wpId)));
  }

  // ---------------------------------------------------------------------------
  // Claims and overlaps
  // ---------------------------------------------------------------------------

  async declareClaim(input: DeclareClaimInput): Promise<DeclareClaimResult> {
    const data = parse(DeclareClaimInputSchema, input);
    this.sweep();
    const s = this.requireLiveSession(data.sessionId);
    const ttl = Math.min(data.ttlMs ?? this.claimTtlMs, this.maxClaimTtlMs);
    const ts = this.iso();
    const claim: IntentClaim = {
      id: this.nextId("clm"),
      sessionId: s.id,
      wpId: s.wpId,
      scope: data.scope,
      rationale: data.rationale,
      mode: data.mode,
      status: "active",
      createdAt: ts,
      expiresAt: new Date(this.clock() + ttl).toISOString(),
    };

    let laneChanged = false;
    if (claim.scope.migration) {
      const entry = { claimId: claim.id, sessionId: s.id, wpId: s.wpId, since: ts };
      if (this.lane.holder === null) {
        this.lane.holder = entry;
        laneChanged = true;
      } else if (this.lane.holder.sessionId !== s.id) {
        claim.status = "queued";
        this.lane.queue.push(entry);
        laneChanged = true;
      }
    }

    // Detect against every other live claim held by another live session. Side A is the existing claim.
    const found: Overlap[] = [];
    for (const other of this.claims.values()) {
      if (other.sessionId === s.id || !LIVE_CLAIM_STATUSES.includes(other.status)) continue;
      if (!this.isLive(other.sessionId)) continue;
      for (const hit of detectScopeOverlaps(other.scope, claim.scope, other.mode, claim.mode)) {
        found.push({
          id: this.nextId("ovl"),
          claimA: other.id,
          claimB: claim.id,
          sessionA: other.sessionId,
          sessionB: s.id,
          kind: hit.kind,
          severity: hit.severity,
          detectedBy: "declared",
          status: "open",
          subjects: hit.subjects,
          summary: `${this.agent(s.id)} (${s.wpId}) and ${this.agent(other.sessionId)} (${other.wpId}) both claim ${hit.kind === "migration" ? "a migration" : `${hit.kind} ${hit.subjects.join(", ")}`}`,
          createdAt: ts,
          updatedAt: ts,
        });
      }
    }

    this.claims.set(claim.id, claim);
    this.claimTtl.set(claim.id, ttl);
    this.emit("claim.declared", { claim, overlapIds: found.map((o) => o.id) });
    for (const o of found) {
      this.overlaps.set(o.id, o);
      this.emit("overlap.detected", { overlap: o });
    }
    if (laneChanged) this.emit("migration_lane.updated", { lane: this.lane });

    // Migrations always sequence: the lane already decides the order, so record it as a rule.
    for (const o of found) {
      if (o.kind !== "migration") continue;
      this.applyResolution(o, {
        overlapId: o.id,
        outcome: "sequence",
        terms: {
          summary: `${this.agent(o.sessionA)} migrates first; ${this.agent(o.sessionB)} waits for the migration lane`,
          order: [o.sessionA, o.sessionB],
        },
        agreedBy: [COORDINATOR_PRINCIPAL],
        via: "rule",
        reason: "Migration numbering is global, so concurrent migrations always sequence in lane order.",
      });
    }
    this.settle();

    const overlaps = found.map((o) => this.overlaps.get(o.id)!);
    return clone({
      claim,
      overlaps,
      blocked: overlaps.some((o) => o.severity === "block" && UNRESOLVED_OVERLAP_STATUSES.includes(o.status)),
    });
  }

  async releaseClaim(input: ReleaseClaimInput): Promise<IntentClaim> {
    const data = parse(ReleaseClaimInputSchema, input);
    this.sweep();
    const claim = this.claims.get(data.claimId);
    if (!claim) throw new CoordinatorError("not_found", `claim ${data.claimId} not found`);
    if (claim.sessionId !== data.sessionId) throw new CoordinatorError("forbidden", "you can only release your own claims");
    if (!LIVE_CLAIM_STATUSES.includes(claim.status)) throw new CoordinatorError("conflict", `claim ${claim.id} is already ${claim.status}`);
    this.endClaim(claim, "released");
    this.settle();
    return clone(claim);
  }

  async checkOverlap(input: CheckOverlapInput): Promise<CheckOverlapResult> {
    const data = parse(CheckOverlapInputSchema, input);
    this.sweep();
    const s = this.requireLiveSession(data.sessionId);
    const candidates: CheckOverlapResult["candidates"] = [];
    for (const other of this.claims.values()) {
      if (other.sessionId === s.id || !LIVE_CLAIM_STATUSES.includes(other.status) || !this.isLive(other.sessionId)) continue;
      for (const hit of detectScopeOverlaps(other.scope, data.scope, other.mode, data.mode)) {
        candidates.push({ ...hit, otherClaimId: other.id, otherSessionId: other.sessionId });
      }
    }
    return {
      candidates,
      wouldBlock: candidates.some((c) => c.severity === "block"),
      wouldQueue: data.scope.migration && this.lane.holder !== null && this.lane.holder.sessionId !== s.id,
    };
  }

  async listClaims(input: ListClaimsInput = {}): Promise<IntentClaim[]> {
    const data = parse(ListClaimsInputSchema, input);
    this.sweep();
    const statuses = data.statuses ?? LIVE_CLAIM_STATUSES;
    return clone(
      [...this.claims.values()].filter(
        (c) => statuses.includes(c.status) && (!data.sessionId || c.sessionId === data.sessionId) && (!data.wpId || c.wpId === data.wpId),
      ),
    );
  }

  async listOverlaps(input: ListOverlapsInput = {}): Promise<Overlap[]> {
    const data = parse(ListOverlapsInputSchema, input);
    this.sweep();
    return clone(
      [...this.overlaps.values()].filter(
        (o) =>
          (!data.sessionId || o.sessionA === data.sessionId || o.sessionB === data.sessionId) &&
          (!data.statuses || data.statuses.includes(o.status)) &&
          (!data.kinds || data.kinds.includes(o.kind)),
      ),
    );
  }

  async getMigrationLane(): Promise<MigrationLane> {
    this.sweep();
    return clone(this.lane);
  }

  // ---------------------------------------------------------------------------
  // Negotiation
  // ---------------------------------------------------------------------------

  async postMessage(input: PostMessageInput): Promise<Message> {
    const data = parse(PostMessageInputSchema, input);
    this.sweep();
    const fromSession = !isHumanPrincipal(data.from) && !isSystemPrincipal(data.from);
    if (fromSession) this.requireLiveSession(data.from);
    if (data.to !== BROADCAST && !this.sessions.has(data.to)) throw new CoordinatorError("not_found", `session ${data.to} not found`);

    const overlap = data.overlapId ? this.overlaps.get(data.overlapId) : undefined;
    if (data.overlapId && !overlap) throw new CoordinatorError("not_found", `overlap ${data.overlapId} not found`);
    const party = overlap ? this.partyOf(overlap, data.from) : null;
    if (overlap && fromSession && !party) throw new CoordinatorError("forbidden", "only the two sessions in an overlap can message about it");

    const replyTo = data.inReplyTo ? this.messages.find((m) => m.id === data.inReplyTo) : undefined;
    if (data.inReplyTo && !replyTo) throw new CoordinatorError("not_found", `message ${data.inReplyTo} not found`);

    let resolution: RecordResolutionInput | null = null;
    if (data.type !== "info") {
      if (!overlap || !party) throw new CoordinatorError("forbidden", `${data.type} must come from a session in the overlap`);
      if (overlap.status !== "open" && overlap.status !== "negotiating") {
        throw new CoordinatorError("conflict", `overlap ${overlap.id} is ${overlap.status}; negotiation is over`);
      }
      if (data.type === "accept" || data.type === "reject" || data.type === "counter") {
        if (!replyTo || replyTo.overlapId !== overlap.id || !replyTo.proposal) {
          throw new CoordinatorError("invalid_input", `${data.type} must reply to a proposal or counter on the same overlap`);
        }
        if (replyTo.from === data.from) throw new CoordinatorError("invalid_input", `you cannot ${data.type} your own proposal`);
      }
      if (data.type === "accept") {
        resolution = {
          ...replyTo!.proposal!,
          overlapId: overlap.id,
          agreedBy: [replyTo!.from, data.from],
          via: "agreement",
          reason: data.body,
        } as RecordResolutionInput;
      } else if (data.type === "yield") {
        const other = party === "A" ? overlap.sessionB : overlap.sessionA;
        resolution = {
          overlapId: overlap.id,
          outcome: "sequence",
          terms: { summary: `${this.agent(data.from)} yields: ${this.agent(other)} lands first`, order: [other, data.from] },
          agreedBy: [data.from],
          via: "yield",
          reason: data.body,
        };
      }
      if (resolution) this.validateResolution(overlap, resolution);
    }

    const message: Message = { id: this.nextId("msg"), ...data, ts: this.iso() };
    this.messages.push(message);
    this.emit("message.posted", { message });
    if (overlap && (data.type.startsWith("propose_") || data.type === "counter" || data.type === "reject") && overlap.status === "open") {
      this.setOverlapStatus(overlap, "negotiating");
    }
    if (overlap && resolution) this.applyResolution(overlap, resolution);
    this.settle();
    for (const wake of this.messageWaiters) wake();
    return clone(message);
  }

  async readMessages(input: ReadMessagesInput): Promise<Message[]> {
    const data = parse(ReadMessagesInputSchema, input);
    this.sweep();
    if (!this.sessions.has(data.sessionId)) throw new CoordinatorError("not_found", `session ${data.sessionId} not found`);
    if (data.overlapId && !this.overlaps.has(data.overlapId)) throw new CoordinatorError("not_found", `overlap ${data.overlapId} not found`);
    if (data.after && !this.messages.some((m) => m.id === data.after)) throw new CoordinatorError("not_found", `message ${data.after} not found`);

    const select = () => {
      const start = data.after ? this.messages.findIndex((m) => m.id === data.after) + 1 : 0;
      return this.messages
        .slice(start)
        .filter((m) => (data.overlapId ? m.overlapId === data.overlapId : m.to === data.sessionId || m.to === BROADCAST))
        .slice(0, data.limit ?? 100);
    };
    let found = select();
    const wait = Math.min(data.waitMs ?? 0, COORDINATOR_DEFAULTS.maxWaitMs);
    if (found.length === 0 && wait > 0) {
      await new Promise<void>((resolve) => {
        const wake = () => {
          if (select().length === 0) return;
          done();
        };
        const timer = setTimeout(() => done(), wait);
        const done = () => {
          clearTimeout(timer);
          this.messageWaiters.delete(wake);
          resolve();
        };
        this.messageWaiters.add(wake);
      });
      found = select();
    }
    return clone(found);
  }

  async recordResolution(input: RecordResolutionInput): Promise<Resolution> {
    const data = parse(RecordResolutionInputSchema, input);
    this.sweep();
    const overlap = this.overlaps.get(data.overlapId);
    if (!overlap) throw new CoordinatorError("not_found", `overlap ${data.overlapId} not found`);
    this.validateResolution(overlap, data);
    const resolution = this.applyResolution(overlap, data);
    this.settle();
    return clone(resolution);
  }

  async listResolutions(input: ListResolutionsInput = {}): Promise<Resolution[]> {
    const data = parse(ListResolutionsInputSchema, input);
    this.sweep();
    return clone(this.resolutions.filter((r) => !data.overlapId || r.overlapId === data.overlapId));
  }

  // ---------------------------------------------------------------------------
  // Plans and packages
  // ---------------------------------------------------------------------------

  async submitPlan(input: SubmitPlanInput): Promise<WorkPlan> {
    const data = parse(SubmitPlanInputSchema, input);
    this.sweep();
    const existing = this.plans.get(data.specId);
    if (existing?.approvedBy) throw new CoordinatorError("conflict", `plan ${data.specId} is already approved`);
    for (const p of data.packages) {
      const owner = this.packageSpec.get(p.id);
      if (owner && owner !== data.specId) throw new CoordinatorError("conflict", `${p.id} already belongs to plan ${owner}`);
    }
    if (existing) for (const p of existing.packages) this.packageSpec.delete(p.id);
    const plan: WorkPlan = {
      ...data,
      packages: data.packages.map((p) => ({ ...p, status: "draft", forkRepo: null, sessionId: null })),
      approvedBy: null,
      approvedAt: null,
      createdAt: existing?.createdAt ?? this.iso(),
    };
    this.plans.set(plan.specId, plan);
    for (const p of plan.packages) this.packageSpec.set(p.id, plan.specId);
    this.emit("plan.submitted", { plan });
    return clone(plan);
  }

  async approvePlan(input: ApprovePlanInput): Promise<WorkPlan> {
    const data = parse(ApprovePlanInputSchema, input);
    this.sweep();
    const plan = this.plans.get(data.specId);
    if (!plan) throw new CoordinatorError("not_found", `plan ${data.specId} not found`);
    if (plan.approvedBy) throw new CoordinatorError("conflict", `plan ${data.specId} is already approved`);
    if (!isHumanPrincipal(data.approvedBy)) throw new CoordinatorError("forbidden", "only a human (human:<handle>) can approve a plan");
    const answers = data.answers ?? {};
    for (const id of Object.keys(answers)) {
      if (!plan.questions.some((q) => q.id === id)) throw new CoordinatorError("invalid_input", `unknown question ${id}`);
    }
    const questions = plan.questions.map((q) => ({ ...q, answer: answers[q.id] ?? q.answer }));
    const open = questions.filter((q) => q.blocking && q.answer === null).map((q) => q.id);
    if (open.length > 0) throw new CoordinatorError("precondition_failed", `blocking questions unanswered: ${open.join(", ")}`);
    plan.questions = questions;
    plan.approvedBy = data.approvedBy;
    plan.approvedAt = this.iso();
    for (const p of plan.packages) p.status = "approved";
    this.emit("plan.approved", { plan });
    for (const p of plan.packages) this.emit("package.updated", { package: p });
    return clone(plan);
  }

  async amendPlan(input: AmendPlanInput): Promise<WorkPlan> {
    const data = parse(AmendPlanInputSchema, input);
    this.sweep();
    const plan = this.plans.get(data.specId);
    if (!plan) throw new CoordinatorError("not_found", `plan ${data.specId} not found`);
    const { plan: next, amendment, changed } = amendPlanRecord(plan, data, this.iso(), (wpId) => this.packageSpec.get(wpId));
    this.plans.set(next.specId, next);
    for (const p of next.packages) this.packageSpec.set(p.id, next.specId);
    this.emit("plan.amended", { plan: next, amendment });
    for (const p of changed) this.emit("package.updated", { package: p });
    return clone(next);
  }

  async getPlan(input: GetPlanInput): Promise<WorkPlan> {
    const data = parse(GetPlanInputSchema, input);
    this.sweep();
    const plan = this.plans.get(data.specId);
    if (!plan) throw new CoordinatorError("not_found", `plan ${data.specId} not found`);
    return clone(plan);
  }

  async getWorkPackage(input: GetWorkPackageInput): Promise<WorkPackage> {
    const data = parse(GetWorkPackageInputSchema, input);
    this.sweep();
    const pkg = this.findPackage(data.wpId);
    if (!pkg) throw new CoordinatorError("not_found", `work package ${data.wpId} not found`);
    return clone(pkg);
  }

  async dispatchPackage(input: DispatchPackageInput): Promise<WorkPackage> {
    const data = parse(DispatchPackageInputSchema, input);
    this.sweep();
    const pkg = this.findPackage(data.wpId);
    if (!pkg) throw new CoordinatorError("not_found", `work package ${data.wpId} not found`);
    if (pkg.status === "draft") throw new CoordinatorError("precondition_failed", `${pkg.id}'s plan is not approved`);
    if (pkg.status !== "approved") throw new CoordinatorError("conflict", `${pkg.id} is already ${pkg.status}`);
    this.updatePackage(pkg, { status: "dispatched", forkRepo: data.forkRepo });
    return clone(pkg);
  }

  // ---------------------------------------------------------------------------
  // Diffs, verdicts and landing
  // ---------------------------------------------------------------------------

  async ingestDiffSummary(input: DiffSummary): Promise<IngestDiffResult> {
    const diff = parse(DiffSummarySchema, input);
    this.sweep();
    const me = this.requireLiveSession(diff.sessionId);
    const mine = this.liveClaimsOf(me.id);
    const ts = this.iso();
    const found: Overlap[] = [];

    for (const other of this.sessions.values()) {
      if (other.id === me.id || !this.isLive(other.id)) continue;
      const theirs = this.liveClaimsOf(other.id);
      const theirDiff = this.latestDiff.get(other.id);
      const subjects = new Set<string>();
      let severity: OverlapSeverity = "warn";
      let claimA: string | null = null;
      let claimB: string | null = null;
      const hit = (subject: string, sev: OverlapSeverity, a: string | null, b: string | null) => {
        subjects.add(subject);
        if (sev === "block") severity = "block";
        claimA ??= a;
        claimB ??= b;
      };

      for (const f of diff.files) {
        const paths = [f.path, ...(f.previousPath ? [f.previousPath] : [])];
        const myCover = (p: string) => mine.find((c) => scopeCoversPath(c.scope, p)) ?? null;

        // (a) Undeclared edits inside another session's claim.
        for (const c of theirs) {
          const sev: OverlapSeverity = c.mode === "exclusive" ? "block" : "warn";
          for (const p of paths) if (scopeCoversPath(c.scope, p) && !myCover(p)) hit(p, sev, c.id, null);
          for (const sym of f.symbols) {
            if (c.scope.symbols.includes(sym) && !mine.some((m) => m.scope.symbols.includes(sym))) hit(sym, sev, c.id, null);
          }
          for (const ref of f.contracts) {
            if (c.scope.contracts.includes(ref) && !mine.some((m) => m.scope.contracts.includes(ref))) hit(ref, sev, c.id, null);
          }
        }

        // (b) Both sessions' pushes touched the same file.
        if (theirDiff) {
          for (const g of theirDiff.files) {
            const gPaths = [g.path, ...(g.previousPath ? [g.previousPath] : [])];
            const shared = paths.find((p) => gPaths.includes(p));
            if (!shared) continue;
            const theirCover = theirs.find((c) => scopeCoversPath(c.scope, shared)) ?? null;
            const mineCover = myCover(shared);
            const commonSymbols = f.symbols.filter((s) => g.symbols.includes(s));
            // Both declared it: the declared overlap already covers this file.
            if (theirCover && mineCover && commonSymbols.length === 0) continue;
            const sev: OverlapSeverity =
              commonSymbols.length > 0 || hunksIntersect(f.hunks, g.hunks) || this.isWholeFile(f) || this.isWholeFile(g) ? "block" : "warn";
            hit(shared, sev, theirCover?.id ?? null, mineCover?.id ?? null);
            for (const sym of commonSymbols) hit(sym, "block", theirCover?.id ?? null, mineCover?.id ?? null);
          }
        }
      }

      // Drop subjects an unreleased diff overlap between these two sessions already reports.
      for (const o of this.overlaps.values()) {
        if (o.kind !== "diff" || o.status === "released") continue;
        if (!((o.sessionA === other.id && o.sessionB === me.id) || (o.sessionA === me.id && o.sessionB === other.id))) continue;
        for (const s of o.subjects) subjects.delete(s);
      }
      if (subjects.size === 0) continue;
      found.push({
        id: this.nextId("ovl"),
        claimA,
        claimB,
        sessionA: other.id,
        sessionB: me.id,
        kind: "diff",
        severity,
        detectedBy: "diff",
        status: "open",
        subjects: [...subjects],
        summary: `${this.agent(me.id)}'s push ${short(diff.commit)} collides with ${this.agent(other.id)} on ${[...subjects].join(", ")}`,
        createdAt: ts,
        updatedAt: ts,
      });
    }

    this.latestDiff.set(me.id, diff);
    this.emit("diff.ingested", { diff, overlapIds: found.map((o) => o.id) });
    for (const o of found) {
      this.overlaps.set(o.id, o);
      this.emit("overlap.detected", { overlap: o });
    }

    // Shared resolutions: tell the other side about every push.
    const notified = new Set<string>();
    for (const r of this.resolutions) {
      if (r.outcome !== "shared") continue;
      const o = this.overlaps.get(r.overlapId);
      if (!o || o.status !== "resolved" || (o.sessionA !== me.id && o.sessionB !== me.id)) continue;
      const other = o.sessionA === me.id ? o.sessionB : o.sessionA;
      if (notified.has(other) || !this.isLive(other)) continue;
      notified.add(other);
      this.postSystemMessage(
        other,
        `${this.agent(me.id)} pushed ${short(diff.commit)} touching ${diff.files.map((f) => f.path).join(", ") || "no files"} (you share: ${r.terms.summary})`,
        o.id,
      );
    }
    this.settle();
    return clone({ diff, overlaps: found.map((o) => this.overlaps.get(o.id)!) });
  }

  async ingestVerdict(input: Verdict): Promise<IngestVerdictResult> {
    const verdict = parse(VerdictSchema, input);
    this.sweep();
    const pkg = this.findPackage(verdict.wpId);
    if (pkg) {
      const expected = new Set(pkg.acceptanceCriteria.map((c) => c.id));
      const given = new Set(verdict.results.map((r) => r.criterionId));
      const missing = [...expected].filter((id) => !given.has(id));
      const unknown = [...given].filter((id) => !expected.has(id));
      if (missing.length || unknown.length) {
        throw new CoordinatorError(
          "invalid_input",
          `verdict must cover exactly ${verdict.wpId}'s criteria (missing: ${missing.join(", ") || "none"}; unknown: ${unknown.join(", ") || "none"})`,
        );
      }
    }
    this.verdicts.push(verdict);
    this.emit("verdict.recorded", { verdict });
    this.settle();
    const land = [...this.lands.values()].reverse().find((l) => l.wpId === verdict.wpId && l.commit === verdict.commit) ?? null;
    return clone({ verdict, land });
  }

  async requestLand(input: RequestLandInput): Promise<LandRequest> {
    const data = parse(RequestLandInputSchema, input);
    this.sweep();
    const s = this.requireLiveSession(data.sessionId);
    const pkg = this.findPackage(s.wpId);
    if (pkg?.status === "landed") throw new CoordinatorError("conflict", `${pkg.id} has already landed`);
    if (pkg?.status === "superseded") throw new CoordinatorError("precondition_failed", `${pkg.id} was superseded by a plan amendment; it never lands`);
    const ts = this.iso();
    const land: LandRequest = {
      id: this.nextId("lnd"),
      sessionId: s.id,
      wpId: s.wpId,
      commit: data.commit,
      status: "pending",
      reasons: [],
      mergeCommit: null,
      conflicts: [],
      createdAt: ts,
      updatedAt: ts,
    };
    for (const old of this.lands.values()) {
      if (old.sessionId === s.id && (old.status === "pending" || old.status === "approved")) {
        this.setLand(old, "rejected", [`superseded by ${land.id}`]);
      }
    }
    this.lands.set(land.id, land);
    if (pkg && pkg.status !== "verifying") this.updatePackage(pkg, { status: "verifying" });
    // Evaluating always changes the new request (pending gains reasons, or it is approved or rejected), so this emits its first event.
    this.evaluateLand(land);
    this.settle();
    return clone(land);
  }

  async reportLandResult(input: ReportLandResultInput): Promise<LandRequest> {
    const data = parse(ReportLandResultInputSchema, input);
    this.sweep();
    const land = this.lands.get(data.landId);
    if (!land) throw new CoordinatorError("not_found", `land ${data.landId} not found`);
    if (land.status !== "approved") throw new CoordinatorError("precondition_failed", `land ${land.id} is ${land.status}, not approved`);
    const pkg = this.findPackage(land.wpId);
    if (data.outcome === "conflict") {
      land.conflicts = data.conflicts ?? [];
      this.setLand(land, "conflict", [`merge conflicts in ${land.conflicts.join(", ")}; rebase on main and request land again`]);
      if (pkg) this.updatePackage(pkg, { status: "in_progress" });
      return clone(land);
    }
    if (data.outcome === "failed") {
      this.setLand(land, "failed", [data.reason ?? "the merge failed"]);
      if (pkg && pkg.status === "verifying") this.updatePackage(pkg, { status: "in_progress" });
      return clone(land);
    }
    land.mergeCommit = data.mergeCommit ?? null;
    this.setLand(land, "merged", []);
    if (pkg) this.updatePackage(pkg, { status: "landed" });
    const s = this.sessions.get(land.sessionId);
    if (s && (s.status === "active" || s.status === "idle")) {
      s.status = "landed";
      this.emit("session.updated", { session: s });
      for (const c of this.liveClaimsOf(s.id)) this.endClaim(c, "released");
      this.releaseOverlapsOf(s.id);
    }
    this.settle();
    return clone(land);
  }

  // ---------------------------------------------------------------------------
  // Deploys
  // ---------------------------------------------------------------------------

  async recordDeploy(input: DeployRecord): Promise<DeployRecord> {
    const data = parse(DeployRecordSchema, input);
    this.sweep();
    const existing = this.deploys.get(data.id);
    if (existing && (existing.stack !== data.stack || existing.kind !== data.kind)) {
      throw new CoordinatorError("conflict", `deploy ${data.id} is a ${existing.kind} of ${existing.stack}`);
    }
    // The deployer does not track approvals; keep one recorded here.
    const record: DeployRecord =
      existing?.approvedBy && data.approvedBy === null ? { ...data, approvedBy: existing.approvedBy, approvedAt: existing.approvedAt } : data;
    this.deploys.set(record.id, record);
    this.emit("deploy.updated", { deploy: record });
    return clone(record);
  }

  async approvePromotion(input: ApprovePromotionInput): Promise<DeployRecord> {
    const data = parse(ApprovePromotionInputSchema, input);
    this.sweep();
    const deploy = this.deploys.get(data.deployId);
    if (!deploy) throw new CoordinatorError("not_found", `deploy ${data.deployId} not found`);
    if (!isHumanPrincipal(data.approvedBy)) throw new CoordinatorError("forbidden", "only a human (human:<handle>) can approve a promotion");
    if (deploy.kind !== "preview" || deploy.status !== "succeeded") {
      throw new CoordinatorError("precondition_failed", "only a succeeded preview can be promoted");
    }
    if (deploy.approvedBy) throw new CoordinatorError("conflict", `deploy ${deploy.id} is already approved`);
    deploy.approvedBy = data.approvedBy;
    deploy.approvedAt = this.iso();
    this.emit("deploy.updated", { deploy });
    return clone(deploy);
  }

  // ---------------------------------------------------------------------------
  // Runs
  // ---------------------------------------------------------------------------

  async reportRun(input: AgentRun): Promise<AgentRun> {
    const data = parse(AgentRunSchema, input);
    this.sweep();
    const existing = this.runs.get(data.id);
    if (existing && existing.wpId !== data.wpId) throw new CoordinatorError("conflict", `run ${data.id} is for ${existing.wpId}`);
    this.runs.set(data.id, data);
    this.emit("run.updated", { run: data });
    return clone(data);
  }

  // ---------------------------------------------------------------------------
  // Observing
  // ---------------------------------------------------------------------------

  async snapshot(): Promise<CoordinatorSnapshot> {
    this.sweep();
    return clone({
      sessions: [...this.sessions.values()],
      claims: [...this.claims.values()],
      overlaps: [...this.overlaps.values()],
      messages: this.messages,
      resolutions: this.resolutions,
      plans: [...this.plans.values()],
      verdicts: this.verdicts,
      lands: [...this.lands.values()],
      deploys: [...this.deploys.values()],
      runs: [...this.runs.values()],
      migrationLane: this.lane,
      lastSeq: this.log.length,
    });
  }

  subscribe(options: SubscribeOptions, listener: (event: PlatformEvent) => void): Subscription {
    const opts = parse(SubscribeOptionsSchema, options);
    const entry: Listener = { types: opts.types ? new Set(opts.types) : null, fn: listener };
    if (opts.since !== undefined) {
      for (const e of this.log) if (e.seq > opts.since && (!entry.types || entry.types.has(e.type))) listener(clone(e));
    }
    this.listeners.add(entry);
    return { close: () => void this.listeners.delete(entry) };
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  private iso(): string {
    return new Date(this.clock()).toISOString();
  }

  private nextId(prefix: string): string {
    const n = (this.counters.get(prefix) ?? 0) + 1;
    this.counters.set(prefix, n);
    return `${prefix}_${String(n).padStart(4, "0")}`;
  }

  private emit<T extends PlatformEventType>(type: T, data: PlatformEventOf<T>["data"]): void {
    const event = { id: this.nextId("evt"), seq: this.log.length + 1, ts: this.iso(), type, data: clone(data) } as PlatformEvent;
    this.log.push(event);
    for (const l of this.listeners) {
      if (l.types && !l.types.has(type)) continue;
      try {
        l.fn(clone(event));
      } catch {
        // A faulty listener must never corrupt coordinator state.
      }
    }
  }

  private agent(sessionId: string): string {
    return this.sessions.get(sessionId)?.agentName ?? sessionId;
  }

  private isLive(sessionId: string): boolean {
    const s = this.sessions.get(sessionId);
    return s !== undefined && (s.status === "active" || s.status === "idle");
  }

  private requireLiveSession(id: string): Session {
    const s = this.sessions.get(id);
    if (!s) throw new CoordinatorError("not_found", `session ${id} not found`);
    if (s.status === "landed" || s.status === "expired") {
      throw new CoordinatorError("precondition_failed", `session ${id} is ${s.status}; register a new session`);
    }
    return s;
  }

  private liveClaimsOf(sessionId: string): IntentClaim[] {
    return [...this.claims.values()].filter((c) => c.sessionId === sessionId && LIVE_CLAIM_STATUSES.includes(c.status));
  }

  private findPackage(wpId: string): WorkPackage | undefined {
    const spec = this.packageSpec.get(wpId);
    return spec ? this.plans.get(spec)?.packages.find((p) => p.id === wpId) : undefined;
  }

  private updatePackage(pkg: WorkPackage, changes: Partial<Pick<WorkPackage, "status" | "forkRepo" | "sessionId">>): void {
    Object.assign(pkg, changes);
    this.emit("package.updated", { package: pkg });
  }

  private isWholeFile(f: DiffFile): boolean {
    return f.change === "added" || f.change === "deleted";
  }

  private partyOf(o: Overlap, principal: string): Party | null {
    if (principal === o.sessionA) return "A";
    if (principal === o.sessionB) return "B";
    return null;
  }

  private setOverlapStatus(o: Overlap, status: Overlap["status"]): void {
    if (o.status === status) return;
    o.status = status;
    o.updatedAt = this.iso();
    this.emit("overlap.updated", { overlap: o });
  }

  private expireSession(s: Session): void {
    s.status = "expired";
    this.emit("session.updated", { session: s });
    for (const c of this.liveClaimsOf(s.id)) this.endClaim(c, "expired");
    this.releaseOverlapsOf(s.id);
  }

  private releaseOverlapsOf(sessionId: string): void {
    for (const o of this.overlaps.values()) {
      if ((o.sessionA === sessionId || o.sessionB === sessionId) && UNRESOLVED_OVERLAP_STATUSES.includes(o.status)) {
        this.setOverlapStatus(o, "released");
      }
    }
  }

  private endClaim(claim: IntentClaim, status: "released" | "expired"): void {
    claim.status = status;
    this.emit("claim.updated", { claim });
    for (const o of this.overlaps.values()) {
      if ((o.claimA === claim.id || o.claimB === claim.id) && UNRESOLVED_OVERLAP_STATUSES.includes(o.status)) {
        this.setOverlapStatus(o, "released");
      }
    }
    if (this.lane.holder?.claimId === claim.id) this.advanceLane();
    else if (this.lane.queue.some((e) => e.claimId === claim.id)) {
      this.lane.queue = this.lane.queue.filter((e) => e.claimId !== claim.id);
      this.emit("migration_lane.updated", { lane: this.lane });
    }
  }

  private advanceLane(): void {
    const next = this.lane.queue.shift();
    this.lane.holder = next ? { ...next, since: this.iso() } : null;
    if (next) {
      const claim = this.claims.get(next.claimId);
      if (claim) {
        claim.status = "active";
        this.emit("claim.updated", { claim });
      }
    }
    this.emit("migration_lane.updated", { lane: this.lane });
    if (next) this.postSystemMessage(next.sessionId, `Your turn in the migration lane: claim ${next.claimId} is now active.`);
  }

  private postSystemMessage(to: string, body: string, overlapId?: string): void {
    const message: Message = { id: this.nextId("msg"), from: COORDINATOR_PRINCIPAL, to, type: "info", body, ts: this.iso() };
    if (overlapId) message.overlapId = overlapId;
    this.messages.push(message);
    this.emit("message.posted", { message });
    for (const wake of this.messageWaiters) wake();
  }

  /** Throws if a resolution is not acceptable for this overlap in its current state. */
  private validateResolution(o: Overlap, r: RecordResolutionInput): void {
    const parties = [o.sessionA, o.sessionB];
    if (o.status === "resolved" || o.status === "released") throw new CoordinatorError("conflict", `overlap ${o.id} is already ${o.status}`);
    if (o.status === "escalated" && (r.via !== "human" || r.outcome === "escalated")) {
      throw new CoordinatorError("conflict", `overlap ${o.id} is escalated; only a human decision (via 'human') can resolve it`);
    }
    const partiesAgreeing = r.agreedBy.filter((p) => parties.includes(p));

    if (r.outcome !== "escalated") {
      const ok =
        (r.via === "agreement" && parties.every((p) => r.agreedBy.includes(p))) ||
        (r.via === "yield" && partiesAgreeing.length > 0) ||
        ((r.via === "timeout" || r.via === "rule") && r.agreedBy.some(isSystemPrincipal)) ||
        (r.via === "human" && r.agreedBy.some(isHumanPrincipal));
      if (!ok) {
        throw new CoordinatorError(
          "forbidden",
          "agreedBy does not match via: agreement needs both sessions, yield the yielding session, timeout/rule a system: principal, human a human: principal",
        );
      }
    }

    switch (r.outcome) {
      case "split": {
        const ids = r.terms.scopes.map((s) => s.sessionId);
        if (new Set(ids).size !== ids.length || !ids.every((id) => parties.includes(id))) {
          throw new CoordinatorError("invalid_input", "split scopes must name sessions in the overlap, once each");
        }
        const claimFor = (party: string) => this.claims.get((party === o.sessionA ? o.claimA : o.claimB) ?? "");
        const scopeFor = (party: string): Scope | undefined => r.terms.scopes.find((s) => s.sessionId === party)?.scope ?? claimFor(party)?.scope;
        for (const id of ids) if (!claimFor(id)) throw new CoordinatorError("invalid_input", `session ${id} has no claim in this overlap to narrow`);
        const a = scopeFor(o.sessionA);
        const b = scopeFor(o.sessionB);
        const kind = o.kind === "diff" ? "path" : o.kind;
        if (a && b && detectScopeOverlaps(a, b).some((hit) => hit.kind === kind)) {
          throw new CoordinatorError("invalid_input", `the split scopes still collide on ${kind}`);
        }
        break;
      }
      case "sequence":
        if (new Set(r.terms.order).size !== 2 || !r.terms.order.every((id) => parties.includes(id))) {
          throw new CoordinatorError("invalid_input", "sequence order must list both sessions in the overlap");
        }
        if (r.via === "yield" && !r.agreedBy.includes(r.terms.order[1]!)) {
          throw new CoordinatorError("forbidden", "only the session that goes second can yield");
        }
        break;
      case "handoff":
        if (r.terms.from === r.terms.to || !parties.includes(r.terms.from) || !parties.includes(r.terms.to)) {
          throw new CoordinatorError("invalid_input", "handoff must be between the two sessions in the overlap");
        }
        if (r.via === "yield" && !r.agreedBy.includes(r.terms.from)) {
          throw new CoordinatorError("forbidden", "only the session handing over can yield");
        }
        break;
      case "shared":
        if (r.via === "yield") throw new CoordinatorError("invalid_input", "yield applies to sequence or handoff only");
        break;
      case "escalated":
        break;
    }
  }

  /** Records a validated resolution and enforces its terms. */
  private applyResolution(o: Overlap, input: RecordResolutionInput): Resolution {
    const resolution = { id: this.nextId("res"), ...clone(input), ts: this.iso() } as Resolution;
    this.resolutions.push(resolution);
    this.setOverlapStatus(o, resolution.outcome === "escalated" ? "escalated" : "resolved");
    this.emit("resolution.recorded", { resolution });
    const claimOf = (session: string) => {
      const id = session === o.sessionA ? o.claimA : o.claimB;
      const claim = id ? this.claims.get(id) : undefined;
      return claim && LIVE_CLAIM_STATUSES.includes(claim.status) ? claim : undefined;
    };
    switch (resolution.outcome) {
      case "split":
        for (const { sessionId, scope } of resolution.terms.scopes) {
          const claim = claimOf(sessionId);
          if (claim) {
            claim.scope = clone(scope);
            this.emit("claim.updated", { claim });
          }
        }
        break;
      case "handoff": {
        const claim = claimOf(resolution.terms.from);
        if (claim) this.endClaim(claim, "released");
        break;
      }
      case "shared":
        for (const session of [o.sessionA, o.sessionB]) {
          const claim = claimOf(session);
          if (claim && claim.mode !== "shared") {
            claim.mode = "shared";
            this.emit("claim.updated", { claim });
          }
        }
        break;
      default:
        break;
    }
    return resolution;
  }

  private setLand(land: LandRequest, status: LandRequest["status"], reasons: string[]): void {
    if (land.status === status && JSON.stringify(land.reasons) === JSON.stringify(reasons)) return;
    land.status = status;
    land.reasons = reasons;
    land.updatedAt = this.iso();
    this.emit("land.updated", { land });
  }

  /** Re-check every pending land request. Called after every state change. */
  private settle(): void {
    for (const land of this.lands.values()) if (land.status === "pending") this.evaluateLand(land);
  }

  private evaluateLand(land: LandRequest): void {
    const reject: string[] = [];
    const wait: string[] = [];
    const verdict = [...this.verdicts].reverse().find((v) => v.wpId === land.wpId && v.commit === land.commit);
    if (verdict && !verdict.pass) {
      const unmet = verdict.results.filter((r) => !r.met).map((r) => r.criterionId);
      const parts = [unmet.length ? `criteria not met: ${unmet.join(", ")}` : "", verdict.regressions.length ? `${verdict.regressions.length} regression(s)` : ""];
      reject.push(`verdict failed (${parts.filter(Boolean).join("; ")})`);
    }
    for (const o of this.overlaps.values()) {
      if ((o.sessionA !== land.sessionId && o.sessionB !== land.sessionId) || o.severity !== "block") continue;
      if (!UNRESOLVED_OVERLAP_STATUSES.includes(o.status)) continue;
      const other = o.sessionA === land.sessionId ? o.sessionB : o.sessionA;
      reject.push(`unresolved blocking ${o.kind} overlap ${o.id} with ${this.agent(other)}: resolve it through the protocol first`);
    }
    for (const r of this.resolutions) {
      if (r.outcome !== "sequence") continue;
      const idx = r.terms.order.indexOf(land.sessionId);
      for (const before of idx > 0 ? r.terms.order.slice(0, idx) : []) {
        const s = this.sessions.get(before);
        if (s && s.status !== "landed" && s.status !== "expired") wait.push(`waiting for ${s.agentName} (${s.wpId}) to land first (${r.id})`);
      }
    }
    const pkg = this.findPackage(land.wpId);
    for (const dep of pkg?.dependsOn ?? []) {
      if (this.findPackage(dep)?.status !== "landed") wait.push(`waiting for dependency ${dep} to land`);
    }
    if (this.liveClaimsOf(land.sessionId).some((c) => c.status === "queued")) wait.push("waiting for the migration lane");
    if (!verdict) wait.push(`awaiting a verdict for ${short(land.commit)}`);

    if (reject.length > 0) {
      this.setLand(land, "rejected", reject);
      if (pkg && pkg.status === "verifying") this.updatePackage(pkg, { status: verdict && !verdict.pass ? "failed" : "in_progress" });
    } else if (wait.length > 0) {
      this.setLand(land, "pending", [...new Set(wait)]);
    } else {
      this.setLand(land, "approved", []);
    }
  }
}
